// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Img, { ImgProps } from "@insite/mobius/Img";
import { Clickable } from "@insite/mobius/index";
import LazyImage, { LazyImageProps, LazyImageStyle } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsState } from "@werner/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@werner/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { color, ellipsis, mergeWithCss, xScreen } from "@werner/XDL/Utilities";
import parser from "html-react-parser";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    image = "image",
    title = "title",
    content = "content",
    url = "url",
    openInNewTab = "openInNewTab",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.title]: string;
        [fields.content]: string;
        [fields.url]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
    };
    extendedStyles?: InfoCardStyles;
}

// Xng-Change
const mapStateToProps = (state: xGoogleAnalyticsState) => ({
    xGoogleAnalytics: state.xGoogleAnalytics,
    isAuthenticated: xIsAuthenticated(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> ;

interface InfoCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: ClickablePresentationProps;
    image?: LazyImageProps;
    contentWrapper?: InjectableCss;
    title?: LinkPresentationProps;
    content?: TypographyPresentationProps;
}

const infoCardStyles: InfoCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            max-width: 420px;
            ${xScreen("lte", "sm")} {
                max-width: 325px;
            }
            ${xScreen("lte", "xs")} {
                max-width: 100%;
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            height: 203px;
            display: flex;
            justify-content: center;
            position: relative;

            &:after {
                content: " ";
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                background: ${color("common.black")};
                opacity: 0;
                transition: all 0.15s linear;
            }
            &:hover {
                &:after {
                    opacity: 0.33;
                }
            }
        `,
    },
    image: {
        css: css`
            height: auto;
            max-height: 100%;
            max-width: 100%;
        `,
    },
    contentWrapper: {
        css: css`
            margin-top: 15px;
        `,
    },
    title: {
        typographyProps: {
            size: 18,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
                text-transform: capitalize;
                max-width: 40ch;
                ${ellipsis(1)};
            `,
        },
        css: css`
            margin-bottom: 12px;
        `,
    },
    content: {
        variant: "p",
        size: 15,
        color: "text.main",
        css: css`
            letter-spacing: 0;
            line-height: 20px;
            margin-top: 5px;
            max-width: 200ch;
            ${ellipsis(5)};

            & > ${TypographyStyle} {
                font-size: inherit;
                font-weight: inherit;
                line-height: inherit;
                margin-bottom: 0;
            }
        `,
    },
};

const InfoCard: FC<Props> = ({ fields, extendedStyles, xGoogleAnalytics, isAuthenticated }) => {
    const [styles] = useState(() => mergeWithCss(infoCardStyles, extendedStyles));
    const link = useGetLink(fields.url);

    // Xng-Change
    // WES-1770
    const sendTracking = () => {
        const dataLayer = getDataLayer();

        if (!dataLayer || !xGoogleAnalytics.trackQuickLinks) {
            return;
        }

        const googleAnalyticsData = {
            event: xGoogleAnalytics.trackQuickLinksEventName,
            click_text: fields.title,
            destination: link.url,
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: isAuthenticated ? "authenticated" : "unauthenticated",
        };

        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="info-card-widget">
            <Clickable {...styles.imageWrapper} href={link.url} target={fields.openInNewTab ? "_blank" : ""} onClick={sendTracking} >
                <LazyImage src={fields.image} altText={fields.title} {...styles.image} />
            </Clickable>
            <StyledWrapper {...styles.contentWrapper}>
                <Link
                    {...styles.title}
                    href={link.url}
                    target={fields.openInNewTab ? "_blank" : ""}
                    aria-label={`${fields.title}`}
                    onClick={sendTracking}
                >
                    {fields.title}
                </Link>

                <Typography {...styles.content} as="div">
                    {parser(fields.content, parserOptions)}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(InfoCard),
    definition: {
        group: "Xngage Custom" as any,
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.image,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.content,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.url,
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                fieldType: "Translatable",
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
